import {validateDataTableStyle} from '@/components/Organization/Profile/ContactList/ContactListTable/ImportContactList/ValidateDataTable/ValidateDataTable.style';
import Button from '@/shared/components/Button';
import {ReactComponent as SuccessIcon} from '@/assets/icons/successfully.svg';
import {OUTLINE} from '@/utils/constants';
import {getErrorMessage} from '@/utils/helpers';
import {OrganizationAttendeesService} from '@/utils/services';
import {
	Box,
	Checkbox,
	Icon,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import {ChangeEvent, FC, useContext, useEffect, useMemo, useState} from 'react';
import {toast} from 'react-toastify';
import {UploadAttendeeFromContactsContext} from '../UploadAttendeeFromContactsContext';
import {STORAGE_KEY} from '@/utils/enums';
import {IOrganizationUpdateAttendeeRequest} from '@/utils/types';
import Loader from '@/shared/components/Loader';
import Modal from '@/shared/components/Modal';
import {useAppSelector} from '@/store/hooks';
import {getCurrentEventSelector} from '@/store/organization-event';

export interface IValidateAttendeeDataTable {
	onClose: () => void;
}

export const ValidateAttendeeDataTable: FC<IValidateAttendeeDataTable> = ({onClose}) => {
	const currentEvent = useAppSelector(getCurrentEventSelector);

	const [duplicates, setDuplicates] = useState([]);

	const [isLoading, setIsLoading] = useState(false);
	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
	const [contactsToDelete, setContactsToDelete] = useState<IOrganizationUpdateAttendeeRequest[] | []>([]);

	const {searchColumn} = useContext(UploadAttendeeFromContactsContext);

	const filteredDuplicates = useMemo(() => {
		const seen = new Set<string>();

		return duplicates.filter((item) => {
			const key = item[searchColumn];
			if (seen.has(key)) return false;
			seen.add(key);

			return true;
		});
	}, [duplicates, searchColumn]);

	const handleConfirmClick = async () => {
		if (!currentEvent) return;
		const updateAttendee = async () => {
			try {
				OrganizationAttendeesService.updateAttendeeFromContactList(
					currentEvent?.id,
					searchColumn,
					filteredDuplicates as IOrganizationUpdateAttendeeRequest[]
				);

				setIsSuccessModalOpen(true);
			} catch (error) {
				toast.error(getErrorMessage(error));
			}
		};
		duplicates.length && updateAttendee();
	};

	useEffect(() => {
		const getData = async () => {
			if (!currentEvent) return;

			try {
				setIsLoading(true);
				const {data} = await OrganizationAttendeesService.getAttendeeDuplicates(currentEvent?.id, searchColumn);
				setDuplicates(data);
			} catch (error) {
				toast.error(getErrorMessage(error));
			} finally {
				setIsLoading(false);
			}
		};
		getData();
	}, []);

	const handleDeleteContact = () => {
		setDuplicates(duplicates.filter((item) => !contactsToDelete.includes(item)));
	};

	const handleChange = (e: ChangeEvent<HTMLInputElement>, row: IOrganizationUpdateAttendeeRequest) => {
		if (e.target.checked) {
			setContactsToDelete((prev) => [...prev, row]);
		} else {
			setContactsToDelete((prev) => prev.filter((item) => item.id !== row.id));
		}
	};

	return (
		<Box sx={{paddingTop: '40px'}}>
			{isLoading ? (
				<Loader parentClassName={validateDataTableStyle.loader} />
			) : duplicates.length > 0 ? (
				<>
					<Box sx={validateDataTableStyle.wrapper}>
						<Typography sx={validateDataTableStyle.title}>Validate data:</Typography>
						<Box sx={validateDataTableStyle.btnWrapper}>
							<Button title="Discard Selected Rows" variant={OUTLINE} onClick={handleDeleteContact} />
						</Box>
					</Box>
					<TableContainer sx={validateDataTableStyle.tableContainer}>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell sx={validateDataTableStyle.tableHead} />
									<TableCell sx={validateDataTableStyle.tableHead}>First name</TableCell>
									<TableCell sx={validateDataTableStyle.tableHead}>Last name</TableCell>
									<TableCell sx={validateDataTableStyle.tableHead}>Email</TableCell>
									<TableCell sx={validateDataTableStyle.tableHead}>Phone</TableCell>
									<TableCell sx={validateDataTableStyle.tableHead}>Meal</TableCell>
									<TableCell sx={validateDataTableStyle.tableHead}>Address</TableCell>
									<TableCell sx={validateDataTableStyle.tableHead}>City</TableCell>
									<TableCell sx={validateDataTableStyle.tableHead}>State</TableCell>
									<TableCell sx={validateDataTableStyle.tableHead}>Zip</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{duplicates.map((row: IOrganizationUpdateAttendeeRequest) => (
									<TableRow key={row.id}>
										<TableCell style={{padding: '0', width: '42px', height: '42px'}}>
											<Checkbox
												onChange={(e) => {
													handleChange(e, row);
												}}
												checked={contactsToDelete.includes(row as never)}
											/>
										</TableCell>
										<TableCell sx={validateDataTableStyle.tableCell}>{row?.firstName}</TableCell>
										<TableCell sx={validateDataTableStyle.tableCell}>{row?.lastName}</TableCell>
										<TableCell sx={validateDataTableStyle.tableCell}>{row?.email}</TableCell>
										<TableCell sx={validateDataTableStyle.tableCell}>{row?.phoneNumber}</TableCell>
										<TableCell sx={validateDataTableStyle.tableCell}>{row?.meals.join(', ')}</TableCell>
										<TableCell sx={validateDataTableStyle.tableCell}>{row?.address}</TableCell>
										<TableCell sx={validateDataTableStyle.tableCell}>{row?.city}</TableCell>
										<TableCell sx={validateDataTableStyle.tableCell}>{row?.state}</TableCell>
										<TableCell sx={validateDataTableStyle.tableCell}>{row?.zip}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
					<Box sx={validateDataTableStyle.buttonWrapper}>
						<Button title="Confirm" onClick={handleConfirmClick} />
					</Box>
				</>
			) : (
				<Box>No duplicates found</Box>
			)}

			{/* <Modal
				open={isWarningModalOpen}
				onClose={handleWarningModal}
				title="Some Rows Contain Errors"
				description="Rows with errors will be ignored when submitting."
				mainIcon={
					<Icon sx={validateDataTableStyle.warningIcon}>
						<WarningIcon />
					</Icon>
				}
				color="warning"
				mainButtonTitle="Continue"
				closeButtonTitle="Cancel"
				onSave={setContactData}
				sx={validateDataTableStyle.modal}
				isShowCloseButton
				isShowSaveButton
			/> */}

			<Modal
				open={isSuccessModalOpen}
				onClose={() => {
					setIsSuccessModalOpen(false);
					onClose();
				}}
				title="Attendees Updated"
				description="Your attendee list has been successfully updated."
				mainIcon={
					<Icon sx={validateDataTableStyle.warningIcon}>
						<SuccessIcon />
					</Icon>
				}
				sx={validateDataTableStyle.modal}
				onSave={() => {
					setIsSuccessModalOpen(false);
					onClose();
				}}
				mainButtonTitle="Done"
				isShowSaveButton
			/>
		</Box>
	);
};
